import React, { useEffect, useState } from 'react';
import { useCompany } from '@/context/CompanyContext';
import { useSecurityMonitoring } from '@/hooks/useSecurityMonitoring';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mail, Clock, XCircle, Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface MaskedInvitation {
  id: string;
  company_id: string;
  email_masked: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
  invited_by: string;
}

export const PendingInvitationsList: React.FC = () => {
  const { userRole, currentCompany, cancelInvitation } = useCompany();
  const { logSecurityEvent } = useSecurityMonitoring();
  const [invitations, setInvitations] = useState<MaskedInvitation[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMaskedInvitations = async () => {
    if (!currentCompany?.id || userRole !== 'admin') return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_secure_team_invitations', {
        p_company_id: currentCompany.id
      });

      if (error) {
        await logSecurityEvent('data_access_failed', {
          table: 'team_invitations',
          error: 'Failed to fetch masked invitations'
        });
        return;
      }

      setInvitations(data || []);
      
      await logSecurityEvent('data_access', {
        table: 'team_invitations',
        action: 'fetch_masked_invitations',
        record_count: data?.length || 0
      });
    } catch (error) {
      await logSecurityEvent('data_access_failed', {
        table: 'team_invitations',
        error: 'Exception while fetching invitations'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaskedInvitations();
  }, [currentCompany?.id, userRole]);

  const handleCancelInvitation = async (invitationId: string) => {
    const success = await cancelInvitation(invitationId);
    if (success) {
      await fetchMaskedInvitations(); // Refresh the list
      await logSecurityEvent('invitation_cancelled', {
        invitation_id: invitationId
      });
    }
  };

  if (userRole !== 'admin') {
    return null;
  }

  const pending = invitations.filter((inv) => inv.status === 'pending');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/20 transition-transform duration-200 hover:scale-105">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1 text-blue-700 dark:text-blue-300">
                Équipe
              </p>
              <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
                Invitations en attente ({pending.length})
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchMaskedInvitations}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Rafraîchir'
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {pending.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune invitation en attente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">
                    {inv.email_masked.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium">{inv.email_masked}</div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge variant="secondary" className="capitalize">{inv.role}</Badge>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Expire le {new Date(inv.expires_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Annuler cette invitation ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        L'invitation envoyée à <strong>{inv.email_masked}</strong> sera supprimée.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleCancelInvitation(inv.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};